/**
 * /knowledge-base/analytics — manager+ only.
 *
 * KPI tiles → top-by-inserts / top-by-views tables → top-agents → trend chart →
 * needs-love (low helpful ratio) → recent thumbs-down comments.
 *
 * Data: single GET /api/kb-analytics call backed by audit_log + kb_articles + kb_votes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart3, Eye, Link2, ThumbsUp, ThumbsDown, AlertTriangle,
  RotateCw, BookOpen, MessageSquareWarning, TrendingUp,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";

export default function KnowledgeBaseAnalytics() {
  const { user, hasPerm } = useAuth();
  const { activeSlug } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const canSee = hasPerm("kb.view_analytics");

  const load = () => {
    setLoading(true);
    api.get("/kb-analytics?days=30")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canSee) load(); }, [canSee, activeSlug]);   // eslint-disable-line

  if (!canSee) {
    return <div className="p-12 text-center text-slate-400 text-sm">You don't have permission to view KB analytics.</div>;
  }

  if (loading || !data) {
    return <div className="p-12 text-center text-slate-400 text-sm">Loading analytics…</div>;
  }

  const { kpis, top_by_inserts, top_by_views, top_agents, trend, needs_love, downvote_comments } = data;

  return (
    <div className="space-y-5" data-testid="kb-analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" /> KB Analytics
            <span className="text-xs text-slate-400 font-normal">last {kpis.window_days} days</span>
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Which playbooks are pulling their weight, and which ones need a refresh.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/knowledge-base"><Button variant="outline" data-testid="back-to-kb-btn">← Back to KB</Button></Link>
          <Button onClick={load} variant="outline" data-testid="kb-analytics-refresh">
            <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Eye}      label="Views"               value={kpis.views_window}   tone="indigo" testid="kpi-views" />
        <KpiTile icon={Link2}    label="Inserts into tickets" value={kpis.inserts_window} tone="violet" testid="kpi-inserts" />
        <KpiTile icon={ThumbsUp} label="Avg helpful ratio"
                 value={kpis.avg_helpful_ratio === null ? "—" : `${Math.round((kpis.avg_helpful_ratio || 0) * 100)}%`}
                 tone="emerald" testid="kpi-ratio" />
        <KpiTile icon={AlertTriangle} label="Stale articles" value={kpis.stale_count} tone="amber" testid="kpi-stale" />
      </div>

      {/* Trend chart */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Daily activity ({kpis.window_days}d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 240 }} data-testid="kb-trend-chart">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }}
                       tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                <RTooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="views" name="Views" stroke="#6366F1" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="inserts" name="Inserts" stroke="#8B5CF6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TopTable title="Most referenced in tickets" testid="kb-top-inserts" rows={top_by_inserts}
                  scoreKey="used_in_tickets_count" scoreLabel="Inserts" />
        <TopTable title="Most viewed" testid="kb-top-views" rows={top_by_views}
                  scoreKey="view_count" scoreLabel="Views" />
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500">
            Top agents using KB ({kpis.window_days}d)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {top_agents.length === 0 ? (
            <div className="text-xs text-slate-400 italic px-4 py-3">No KB inserts yet in this window.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Agent</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Inserts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top_agents.map((a) => (
                  <TableRow key={a.email} data-testid={`kb-top-agent-${a.email}`}>
                    <TableCell className="text-sm">
                      <div className="font-medium text-slate-800">{a.name || a.email}</div>
                      <div className="text-[10px] text-slate-500">{a.email}</div>
                    </TableCell>
                    <TableCell className="text-right text-sm font-bold text-indigo-700">{a.insert_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Articles needing love
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {needs_love.length === 0 ? (
              <div className="text-xs text-slate-400 italic px-4 py-3">All articles in good shape (helpful-ratio ≥ 60% on ≥3 votes).</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Title</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Ratio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {needs_love.map((a) => (
                    <TableRow key={a.id} data-testid={`kb-needs-love-${a.id}`}>
                      <TableCell className="text-sm">
                        <Link to={`/knowledge-base/${a.id}`}
                              className="font-medium text-slate-800 hover:text-indigo-700">{a.title}</Link>
                        <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                          <span className="inline-flex items-center gap-0.5"><ThumbsUp className="w-2.5 h-2.5" />{a.helpful_count || 0}</span>
                          <span className="inline-flex items-center gap-0.5"><ThumbsDown className="w-2.5 h-2.5" />{a.unhelpful_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                          {Math.round(a.helpful_ratio * 100)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center gap-2">
              <MessageSquareWarning className="w-3.5 h-3.5 text-rose-600" /> Recent thumbs-down comments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {downvote_comments.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No detailed feedback yet.</div>
            ) : (
              downvote_comments.map((c) => (
                <div key={c.id} className="rounded-md border border-rose-100 bg-rose-50/50 px-3 py-2"
                     data-testid={`kb-downvote-${c.id}`}>
                  <div className="text-xs font-medium text-slate-700">
                    <Link to={`/knowledge-base/${c.article_id}`} className="hover:text-indigo-700">{c.article_title}</Link>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5 italic">"{c.comment}"</div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    — {c.user_name || "anonymous"} · {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-md grid place-items-center border ${tones[tone] || ""}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
          <div className="text-xl font-bold text-slate-900">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function TopTable({ title, rows, scoreKey, scoreLabel, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.filter((r) => (r[scoreKey] || 0) > 0).length === 0 ? (
          <div className="text-xs text-slate-400 italic px-4 py-3">No data yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Title</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">{scoreLabel}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`${testid}-row-${r.id}`}>
                  <TableCell className="text-sm">
                    <Link to={`/knowledge-base/${r.id}`} className="font-medium text-slate-800 hover:text-indigo-700">
                      {r.title}
                    </Link>
                    {r.category && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{r.category}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm font-bold text-indigo-700">
                    {r[scoreKey] || 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
